import type { Edge, Neighbor, Station } from '@/types';
import { STATIONS as RAW_STATIONS } from './stations';
import { EDGES as RAW_EDGES } from './edges';

/**
 * Construction et exposition du graphe routable.
 *
 * À l'import de ce module :
 * 1. On filtre les arêtes orphelines (référencent une station inexistante).
 *    Ça arrive si on supprime une ville sans nettoyer les arêtes — au lieu de
 *    crasher au runtime, on retire silencieusement la donnée morte.
 * 2. On construit `STATION_INDEX` : map id → indice dans STATIONS pour accès O(1).
 * 3. On construit `GRAPH` : map id → liste de voisins (graphe non orienté).
 *
 * Tout cela est fait une seule fois à l'initialisation, puis exporté.
 * Les modules `core/` consomment GRAPH/STATIONS/STATION_INDEX sans connaître
 * la source réelle des données.
 */

const _validIds = new Set(RAW_STATIONS.map((s) => s.id));

/**
 * Arêtes valides : on retire celles qui référencent une station inexistante.
 * Exemple : "minsk" est cité dans une arête mais pas dans STATIONS → filtré ici.
 */
export const EDGES: Edge[] = RAW_EDGES.filter(
  (e) => _validIds.has(e.a) && _validIds.has(e.b),
);

/** Liste finale des stations (référence directe vers RAW_STATIONS, ré-exportée pour ergonomie) */
export const STATIONS: Station[] = RAW_STATIONS;

/** Index pour accès O(1) à une station par son id */
export const STATION_INDEX: Record<string, number> = Object.freeze(
  RAW_STATIONS.reduce<Record<string, number>>((acc, s, i) => {
    acc[s.id] = i;
    return acc;
  }, {}),
);

/**
 * Graphe d'adjacence non orienté.
 * Pour chaque station, la liste de ses voisins atteignables en un saut.
 * Chaque arête est dupliquée dans les deux sens (A↔B = A→B + B→A).
 */
export const GRAPH: Record<string, Neighbor[]> = (() => {
  const g: Record<string, Neighbor[]> = {};
  RAW_STATIONS.forEach((s) => {
    g[s.id] = [];
  });
  EDGES.forEach((e) => {
    // nightBus est optionnel sur Edge — on normalise à false ici pour que
    // le reste du code n'ait pas à se soucier de undefined.
    const nb = e.nightBus === true;
    g[e.a].push({
      to: e.b,
      train: e.train,
      bus: e.bus,
      direct: e.direct,
      night: e.night,
      nightBus: nb,
      op: e.op,
    });
    g[e.b].push({
      to: e.a,
      train: e.train,
      bus: e.bus,
      direct: e.direct,
      night: e.night,
      nightBus: nb,
      op: e.op,
    });
  });
  return g;
})();
